import { html } from 'lit';
import { DataTableConfig, ColumnDefinition, FilterDefinition, ActionDefinition } from '../../common/data-table/index.js';
import { WorkOrderModel, WorkOrderStatusCat, WorkOrderPriorityCat } from '@cmms/models';
import { WorkOrder } from '../../../types/work-order.types.js';

export function createWorkOrdersTableConfig(
  workOrders: WorkOrder[],
  assets: any[],
  users: any[],
  handlers: {
    onRowClick: (workOrder: WorkOrder) => void;
    onDelete: (workOrder: WorkOrder) => void;
    onAddWorkOrder?: () => void;
    onFilterChange?: (filters: Record<string, any>) => void;
    onSort?: (column: string, direction: 'asc' | 'desc') => void;
  }
): DataTableConfig<WorkOrder> {

  const getAssetName = (id?: string) => {
    if (!id) return '—';
    const found = (assets || []).find(
      a => a.id === id || a.$id === id || a.asset_id === id
    );
    if (!found) return '—';
    
    return (
      found.name ||
      found.model?.name ||
      found.display_name ||
      found.title ||
      found.asset_name ||
      `Asset ${found.id || id}`
    );
  };

  const getUserName = (id?: string) => {
    if (!id) return '—';
    const found = (users || []).find(
      u => u.id === id || u.$id === id || u.user_id === id || u.uid === id
    );
    if (!found) return '—';
    
    return (
      found.model?.name ||
      found.model?.display_name ||
      found.name ||
      found.display_name ||
      found.model?.email ||
      found.email ||
      `User ${found.id || id}`
    );
  };

  const getStatusBadge = (cat: number | string) => {
    const numCat = typeof cat === 'number' ? cat : (Number(cat) || WorkOrderStatusCat.OPEN);
    const item = WorkOrderModel.statusCats.find(s => s.cat === numCat);
    const label = item?.name || `Status ${numCat}`;
    
    let variant = 'primary';
    if (numCat === WorkOrderStatusCat.COMPLETED) variant = 'success';
    else if (numCat === WorkOrderStatusCat.IN_PROGRESS) variant = 'warning';
    
    return html`<sl-badge pill variant=${variant}>${label}</sl-badge>`;
  };

  const getPriorityBadge = (cat: number | string) => {
    const numCat = typeof cat === 'number' ? cat : (Number(cat) || WorkOrderPriorityCat.MEDIUM);
    const item = WorkOrderModel.priorityCats.find(p => p.cat === numCat);
    const label = item?.name || `Priority ${numCat}`;
    
    let variant = 'warning'; // default for MEDIUM
    if (numCat === WorkOrderPriorityCat.CRITICAL || numCat === WorkOrderPriorityCat.HIGH) {
      variant = 'danger';
    } else if (numCat === WorkOrderPriorityCat.LOW) {
      variant = 'neutral';
    }
    
    return html`<sl-badge pill variant=${variant}>${label}</sl-badge>`;
  };

  const truncate = (text = '', max = 64) => {
    const t = `${text}`.trim();
    return t.length > max ? `${t.slice(0, max - 1)}…` : t;
  };

  const formatDate = (ts?: any): string => {
    if (ts == null || ts === undefined) return '—';
    
    let millis: number;
    if (typeof ts === 'number') {
      millis = ts < 1e12 ? ts * 1000 : ts;
    } else if (ts?.toMillis && typeof ts.toMillis === 'function') {
      millis = ts.toMillis();
    } else if (ts?.seconds) {
      millis = ts.seconds * 1000;
    } else {
      return '—';
    }
    
    try {
      const d = new Date(millis);
      if (isNaN(d.getTime())) return '—';
      
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch {
      return '—';
    }
  };

  const columns: ColumnDefinition<WorkOrder>[] = [
    {
      key: 'status_cat',
      label: 'Status',
      type: 'custom',
      sortable: true,
      renderer: (value: number) => getStatusBadge(value)
    },
    {
      key: 'priority_cat',
      label: 'Priority',
      type: 'custom',
      sortable: true,
      renderer: (value: number) => getPriorityBadge(value)
    },
    {
      key: 'asset_id',
      label: 'Asset',
      type: 'text',
      sortable: true,
      formatter: (value: string) => getAssetName(value)
    },
    {
      key: 'assigned_to',
      label: 'Assigned To',
      type: 'text',
      sortable: true,
      formatter: (value: string) => getUserName(value)
    },
    {
      key: 'created_at',
      label: 'Created',
      type: 'date',
      sortable: true,
      className: 'created-col',
      formatter: formatDate
    }
  ];

  const filters: FilterDefinition[] = [
    {
      key: 'status_cat',
      type: 'select',
      label: 'Status',
      placeholder: 'All Status',
      options: WorkOrderModel.statusCats.map(s => ({ value: String(s.cat), label: s.name }))
    },
    {
      key: 'priority_cat',
      type: 'select',
      label: 'Priority',
      placeholder: 'All Priority',
      options: WorkOrderModel.priorityCats.map(p => ({ value: String(p.cat), label: p.name }))
    },
    {
      key: 'asset_id',
      type: 'select',
      label: 'Asset',
      placeholder: 'All Assets',
      options: (assets || []).map(a => ({ 
        value: a.id || a.$id, 
        label: getAssetName(a.id || a.$id) 
      }))
    },
    {
      key: 'assigned_to',
      type: 'select',
      label: 'User',
      placeholder: 'All Users',
      options: (users || []).map(u => ({ 
        value: u.id || u.$id || u.uid, 
        label: getUserName(u.id || u.$id || u.uid) 
      }))
    }
  ];

  const rowActions: ActionDefinition<WorkOrder>[] = [
    {
      key: 'delete',
      label: 'Delete',
      variant: 'danger',
      icon: 'trash',
      size: 'small',
      onClick: (workOrder: WorkOrder, e: Event) => {
        e.stopPropagation();
        handlers.onDelete(workOrder);
      }
    }
  ];

  const headerActions: ActionDefinition[] = handlers.onAddWorkOrder ? [
    {
      key: 'add',
      label: 'Add Work Order',
      variant: 'primary',
      icon: 'plus',
      size: 'medium',
      onClick: () => handlers.onAddWorkOrder!()
    }
  ] : [];

  return {
    columns,
    data: workOrders,
    filters,
    rowActions,
    headerActions,
    searchPlaceholder: 'Search description, asset, assignee or ID…',
    pagination: {
      enabled: true,
      dynamic: true
    },
    emptyState: {
      icon: 'tools',
      message: 'No work orders found. Try adjusting filters or click "Add Work Order".'
    },
    clickableRows: true,
    defaultSort: {
      key: 'created_at',
      direction: 'desc'
    },
    defaultFilters: {
      status_cat: '1' // Default to "Open" status to exclude completed work orders
    },
    onRowClick: handlers.onRowClick,
    onFilterChange: handlers.onFilterChange,
    onSort: handlers.onSort
  };
}
